import { Controller } from "@hotwired/stimulus"
import { apiGet, apiPost, apiDelete } from "../shared/api"
import { escapeHtml, formatDate, loadTranslations, t } from "../shared/utils"

export default class extends Controller {
  static targets = ["content"]
  static values = { newUrl: String }

  connect() {
    loadTranslations()
    this._load()
  }

  async _load() {
    try {
      const json = await apiGet("/api/v1/marketplace/mine")
      this.listings = json.data
      this._render()
    } catch (error) {
      console.error("Error loading listings:", error)
      this.contentTarget.innerHTML = `<div class="alert alert-danger">${escapeHtml(error.message)}</div>`
    }
  }

  _render() {
    if (this.listings.length === 0) {
      this.contentTarget.innerHTML = `<div class="card"><div class="card-body text-center py-5">
        <i class="fa-solid fa-box-open fa-3x text-muted mb-3"></i>
        <p class="text-muted">${escapeHtml(t("no_listings"))}</p>
        <a href="${escapeHtml(this.newUrlValue)}" class="default-btn cta-primary"><i class="fa-solid fa-plus me-2"></i>${escapeHtml(t("add_listing"))}</a>
      </div></div>`
      return
    }

    const rows = this.listings.map(l => this._renderRow(l)).join("")
    this.contentTarget.innerHTML = `<div class="card"><div class="card-body p-0">
      <div class="table-responsive">
        <table class="table table-hover mb-0">
          <thead><tr>
            <th>${escapeHtml(t("title", { default: "Tytuł" }))}</th>
            <th>${escapeHtml(t("categories_label"))}</th>
            <th>Status</th>
            <th>${escapeHtml(t("common.actions", { default: "Akcje" }))}</th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    </div></div>`
  }

  _renderRow(l) {
    const statusBadge = this._statusBadge(l)
    const actions = this._actionButtons(l)

    return `<tr>
      <td><a href="/marketplace/${l.id}">${escapeHtml(l.title)}</a></td>
      <td>${escapeHtml(t(`categories.${l.category}`))}</td>
      <td>${statusBadge}</td>
      <td class="text-end">${actions}</td>
    </tr>`
  }

  _statusBadge(l) {
    switch (l.status) {
      case "active": return `<span class="badge bg-success">${escapeHtml(t("enums.marketplace_listing.status.active", { default: "Aktywne" }))}</span>`
      case "expired": return `<span class="badge bg-danger">${escapeHtml(t("enums.marketplace_listing.status.expired", { default: "Wygasłe" }))}</span>`
      case "draft": return `<span class="badge bg-warning text-dark">${escapeHtml(t("enums.marketplace_listing.status.draft", { default: "Szkic" }))}</span>`
      case "closed": return `<span class="badge bg-secondary">${escapeHtml(t("enums.marketplace_listing.status.closed", { default: "Zamknięte" }))}</span>`
      default: return `<span class="badge bg-secondary">${escapeHtml(l.status)}</span>`
    }
  }

  _actionButtons(l) {
    let html = `<a href="/marketplace/${l.id}" class="btn btn-sm btn-outline-secondary me-1" title="${escapeHtml(t("common.show", { default: "Zobacz" }))}"><i class="fa-solid fa-eye"></i></a>`
    html += `<a href="/marketplace/${l.id}/edit" class="btn btn-sm btn-outline-secondary me-1" title="${escapeHtml(t("common.edit", { default: "Edytuj" }))}"><i class="fa-solid fa-pen"></i></a>`

    if (l.status === "draft") {
      html += `<button class="btn btn-sm btn-primary me-1" data-action="click->marketplace-mine#publishListing" data-listing-id="${l.id}">${escapeHtml(t("publish"))}</button>`
    } else if (l.status === "active") {
      html += `<button class="btn btn-sm btn-outline-secondary me-1" data-action="click->marketplace-mine#renewListing" data-listing-id="${l.id}">${escapeHtml(t("renew"))}</button>`
      html += `<button class="btn btn-sm btn-outline-secondary me-1" data-action="click->marketplace-mine#closeListing" data-listing-id="${l.id}">${escapeHtml(t("close"))}</button>`
    }

    html += `<button class="btn btn-sm btn-outline-danger" data-action="click->marketplace-mine#deleteListing" data-listing-id="${l.id}"><i class="fa-solid fa-trash"></i></button>`
    return html
  }

  async publishListing(e) {
    const id = e.currentTarget.dataset.listingId
    try {
      await apiPost(`/api/v1/marketplace/${id}/publish`)
      this._load()
    } catch (err) { alert(err.message) }
  }

  async renewListing(e) {
    const id = e.currentTarget.dataset.listingId
    try {
      await apiPost(`/api/v1/marketplace/${id}/renew`)
      this._load()
    } catch (err) { alert(err.message) }
  }

  async closeListing(e) {
    const id = e.currentTarget.dataset.listingId
    try {
      await apiPost(`/api/v1/marketplace/${id}/close`)
      this._load()
    } catch (err) { alert(err.message) }
  }

  async deleteListing(e) {
    const id = e.currentTarget.dataset.listingId
    if (!confirm(t("common.delete", { default: "Czy na pewno chcesz usunąć?" }))) return
    try {
      await apiDelete(`/api/v1/marketplace/${id}`)
      this._load()
    } catch (err) { alert(err.message) }
  }
}
